import { computed, ref, watch } from 'vue'

import api from '@/store/api'

export const rangeOptions = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 }
]

export const PAGE_SIZE = 25

// The list can be read two ways: numbered pages (goToPage, with page, pageCount
// and totalCount) or an ever-growing list (loadMore with hasNextPage).
export function useAgentOverview({ agentId, endpoint }) {
  const rangeKey = ref(30)
  const error = ref('')
  const loadingChart = ref(true)
  const loadingList = ref(true)
  const periods = ref([])
  const totals = ref({ conversations: 0, items_added: 0, revenue_cents: 0, handoffs: 0 })
  const tiles = ref(null)
  const rows = ref([])
  const cursor = ref(0)
  const hasNextPage = ref(false)
  const page = ref(1)
  const totalCount = ref(0)

  const pageCount = computed(() => Math.max(1, Math.ceil(totalCount.value / PAGE_SIZE)))

  const dateRange = computed(() => {
    const end = new Date()
    const begin = new Date()
    begin.setDate(end.getDate() - rangeKey.value)
    return { beginning_date: begin.toISOString().slice(0, 10), ending_date: end.toISOString().slice(0, 10) }
  })

  const overviewQuery = () => {
    const query = new URLSearchParams({
      beginning_date: dateRange.value.beginning_date,
      ending_date: dateRange.value.ending_date,
      cursor: cursor.value,
      limit: PAGE_SIZE
    })
    if (agentId.value) query.set('agent_id', agentId.value)
    return query
  }

  // append: add the next batch to the rows already shown (loadMore).
  // toPage: replace the rows with that numbered page; the chart and tiles cover
  // the whole range, so they are left alone unless the range itself changed.
  const fetchOverview = async ({ append = false, toPage = 1, reloadChart = true } = {}) => {
    if (!append) {
      if (reloadChart) loadingChart.value = true
      cursor.value = (toPage - 1) * PAGE_SIZE
    }
    loadingList.value = true
    error.value = ''
    try {
      const res = await api.call('GET', `${endpoint}?${overviewQuery()}`)
      const json = res?.data ?? res
      periods.value = json.periods || []
      totals.value = json.totals || totals.value
      if (!append) tiles.value = json.tiles || null
      if (append) {
        rows.value = [...rows.value, ...(json.list?.data || [])]
      } else {
        rows.value = json.list?.data || []
        page.value = toPage
      }
      totalCount.value = json.list?.total_count || 0
      hasNextPage.value = json.list?.has_next_page || false
      if (hasNextPage.value && json.list?.next_cursor !== null && json.list?.next_cursor !== undefined) {
        cursor.value = json.list.next_cursor
      }
    } catch (e) {
      error.value = e?.response?.data?.errors?.[0] || 'Failed to load overview.'
    } finally {
      loadingChart.value = false
      loadingList.value = false
    }
  }

  const loadMore = () => fetchOverview({ append: true })

  const goToPage = toPage => fetchOverview({ toPage, reloadChart: false })

  watch([rangeKey, agentId], () => fetchOverview(), { immediate: true })

  const refetch = () => fetchOverview({ toPage: page.value })

  return {
    rangeKey,
    rangeOptions,
    error,
    loadingChart,
    loadingList,
    periods,
    totals,
    tiles,
    rows,
    hasNextPage,
    page,
    pageCount,
    totalCount,
    dateRange,
    fetchOverview,
    loadMore,
    goToPage,
    refetch
  }
}
